import { Controller } from "@hotwired/stimulus";
import { Crepe } from "@milkdown/crepe";
import {
  linkTooltipAPI,
  toggleLinkCommand,
} from "@milkdown/kit/component/link-tooltip";
import { editorViewCtx } from "@milkdown/kit/core";
import { linkSchema } from "@milkdown/kit/preset/commonmark";
import { EditorState } from "@codemirror/state";
import {
  EditorView,
  drawSelection,
  highlightActiveLine,
  highlightSpecialChars,
  keymap,
} from "@codemirror/view";
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from "@codemirror/commands";
import { markdown } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import {
  syntaxHighlighting,
  defaultHighlightStyle,
} from "@codemirror/language";
import { oneDarkHighlightStyle } from "@codemirror/theme-one-dark";

export default class extends Controller {
  static targets = [
    "editor",
    "markdownEditor",
    "input",
    "modeOption",
    "linkPopover",
    "linkQuery",
    "linkResults",
  ];
  static values = {
    userName: String,
    wikiName: String,
    wikiId: Number,
    searchPath: String,
    mode: String,
    canonicalUrl: String,
  };

  connect() {
    this._connected = true;
    this._setupToken = (this._setupToken || 0) + 1;
    this._syncTimer = null;
    this._boundEditorClick = this._onEditorClick.bind(this);
    if (this.hasEditorTarget)
      this.editorTarget.addEventListener("click", this._boundEditorClick, true);

    this._boundExternalSetMode = async (e) => {
      const desired = e?.detail?.mode;
      if (!desired) return;
      await this._setMode(desired, { updateUrl: true });
    };
    window.addEventListener(
      "wittle:milkdown:setMode",
      this._boundExternalSetMode
    );

    const initialMode = this._initialMode();
    this._mode = initialMode;
    this._applyModeToUI();

    this._linkPopoverOpen = false;
    this._pendingLink = null;
    this._linkSearchTimer = null;
    this._linkSearchAbort = null;
    this._linkItems = [];
    this._linkSelectedIndex = -1;
    this._boundDocPointerDown = null;

    if (initialMode === "rich") {
      this._setup();
    } else {
      this._setupMarkdown();
    }
  }

  async disconnect() {
    this._connected = false;
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
      this._syncTimer = null;
    }
    if (this._boundEditorClick && this.hasEditorTarget) {
      this.editorTarget.removeEventListener(
        "click",
        this._boundEditorClick,
        true
      );
      this._boundEditorClick = null;
    }
    if (this._boundSync) {
      this.editorTarget.removeEventListener("input", this._boundSync, true);
      this.editorTarget.removeEventListener("keyup", this._boundSync, true);
      this.editorTarget.removeEventListener("blur", this._boundSync, true);
      this._boundSync = null;
    }
    if (this._boundSubmit && this._form) {
      this._form.removeEventListener("submit", this._boundSubmit);
      this._boundSubmit = null;
      this._form = null;
    }
    if (this.crepe) {
      try {
        await this.crepe.destroy();
      } catch (_) {
        // noop
      }
      this.crepe = null;
    }

    this._teardownLinkKeyHandler();
    this._abortLinkSearch();
    this._pendingLink = null;
    if (this._linkSearchTimer) {
      clearTimeout(this._linkSearchTimer);
      this._linkSearchTimer = null;
    }
    this._hideLinkPopover();
    this._detachOutsideClick();

    if (this._boundExternalSetMode) {
      window.removeEventListener(
        "wittle:milkdown:setMode",
        this._boundExternalSetMode
      );
      this._boundExternalSetMode = null;
    }

    this._destroyMarkdownEditor();
  }

  async switchMode(event) {
    const desired = event?.currentTarget?.dataset?.mode;
    if (!desired) return;
    await this._setMode(desired, { updateUrl: true });
  }

  _syncFromEditorNow() {
    if (!this._connected || !this.crepe) return;
    try {
      this.inputTarget.value = this.crepe.getMarkdown();
    } catch (_) {
      // If editor isn't ready yet, ignore.
    }
  }

  _scheduleSync() {
    if (!this._connected || !this.crepe) return;
    // Use a small timeout instead of requestAnimationFrame; rAF can be throttled in headless browsers,
    // which makes system tests flaky and can delay syncing indefinitely.
    if (this._syncTimer) clearTimeout(this._syncTimer);
    this._syncTimer = setTimeout(() => {
      this._syncTimer = null;
      this._syncFromEditorNow();
    }, 50);
  }

  async _setup() {
    const token = this._setupToken;
    if (!this.hasEditorTarget || !this.hasInputTarget) return;
    if (this.crepe) return;

    // If the editor is in markdown mode, do not mount the rich editor.
    if (this._mode === "markdown") return;

    this.editorTarget.style.display = "";
    this.inputTarget.style.display = "none";

    this.editorTarget.classList.add("milkdown");
    this.editorTarget.innerHTML = "";

    try {
      this.crepe = new Crepe({
        root: this.editorTarget,
        defaultValue: this.inputTarget.value || "",
        // We provide our own link UI (Bootstrap modal) instead of the built-in link tooltip.
        features: {
          [Crepe.Feature.LinkTooltip]: false,
        },
      });

      // The toolbar link button uses `toggleLinkCommand` from the link-tooltip package.
      // Even though we disable the tooltip UI, we still need to register the command + ctx.
      this.crepe.editor.use([linkTooltipAPI, toggleLinkCommand].flat());

      // Configure the link command used by the toolbar (and other call sites) to open our modal.
      // The default `toggleLinkCommand` delegates to linkTooltipAPI.{addLink,removeLink,editLink}.
      this.crepe.editor.config((ctx) => {
        const open = (from, to) => this._openLinkPopover({ from, to });
        const api = {
          addLink: (from, to) => open(from, to),
          editLink: (_mark, from, to) => open(from, to),
          // `toggleLinkCommand` calls removeLink if the selection already has a link mark.
          // We treat that as "edit existing link" and let the user remove via the modal button.
          removeLink: (from, to) => open(from, to),
        };

        // Depending on which plugins are enabled, the ctx may or may not have been injected yet.
        // Prefer inject when missing; otherwise set.
        try {
          ctx.inject(linkTooltipAPI, api);
        } catch (_) {
          // ignore (already injected)
        }
        try {
          ctx.set(linkTooltipAPI.key, api);
        } catch (_) {
          // ignore (if inject above didn't run, this will be caught by our outer try/catch)
        }
      });

      // Some environments can trigger connect/disconnect during Turbo visits. Avoid
      // continuing setup if this controller has been replaced.
      if (!this._connected || token !== this._setupToken) return;

      await this.crepe.create();
    } catch (e) {
      try {
        // Make failures visible (system tests + real users), but still gracefully fall back.
        console.error("Milkdown/Crepe failed to mount rich editor", e);
      } catch (_) {
        // ignore
      }
      // Fallback: show the textarea and skip the rich editor.
      this.inputTarget.style.display = "";
      this.editorTarget.classList.remove("milkdown");
      this.editorTarget.innerHTML = "";
      this.crepe = null;
      return;
    }

    if (!this._connected || token !== this._setupToken) {
      try {
        await this.crepe?.destroy?.();
      } catch (_) {
        // noop
      }
      this.crepe = null;
      return;
    }

    // Fallback sync: in some environments markdownUpdated can be delayed/not fired.
    // Keep the underlying textarea up-to-date for form submission.
    this._boundSync = this._scheduleSync.bind(this);
    this.editorTarget.addEventListener("input", this._boundSync, true);
    this.editorTarget.addEventListener("keyup", this._boundSync, true);
    this.editorTarget.addEventListener("blur", this._boundSync, true);

    this._form = this.element.closest("form");
    if (this._form) {
      this._boundSubmit = () => this._syncFromEditorNow();
      this._form.addEventListener("submit", this._boundSubmit);
    }

    this._setupLinkKeyHandler();
    this._syncFromEditorNow();
  }

  // --- Link popover / autocomplete helpers ---

  onLinkQueryInput() {
    if (!this._connected) return;
    if (!this.hasLinkQueryTarget || !this.hasLinkResultsTarget) return;

    const q = this.linkQueryTarget.value.toString().trim();

    if (this._linkSearchTimer) clearTimeout(this._linkSearchTimer);
    this._linkSearchTimer = setTimeout(() => {
      this._linkSearchTimer = null;
      this._runLinkAutocomplete(q);
    }, 120);
  }

  onLinkQueryKeydown(event) {
    if (!this._connected) return;
    if (!this._linkPopoverOpen) return;
    if (!event) return;

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this._hideLinkPopover();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      const chosen = this._linkItems?.[this._linkSelectedIndex];
      if (chosen && chosen.page) {
        this._confirmPageResult(chosen.page);
      } else {
        this._confirmTypedLink();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      this._moveLinkSelection(1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      this._moveLinkSelection(-1);
      return;
    }
  }

  async _runLinkAutocomplete(q) {
    if (!this._connected) return;
    if (!this.hasSearchPathValue || !this.hasWikiIdValue) return;
    if (!Number.isFinite(this.wikiIdValue)) return;
    if (!this.hasLinkResultsTarget) return;

    const query = q.toString().trim();
    if (query === "") {
      this._renderLinkResults([]);
      return;
    }

    this._abortLinkSearch();
    const abort = new AbortController();
    this._linkSearchAbort = abort;

    const params = new URLSearchParams();
    params.set("q", query);
    params.set("scope", "wiki");
    params.set("wiki_id", String(this.wikiIdValue));
    params.set("types", "page");
    params.set("limit", "10");

    try {
      const res = await fetch(
        `${this.searchPathValue}.json?${params.toString()}`,
        {
          headers: { Accept: "application/json" },
          signal: abort.signal,
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const pages = Array.isArray(data?.results?.pages)
        ? data.results.pages
        : [];
      this._renderLinkResults(pages);
    } catch (e) {
      if (e?.name === "AbortError") return;
      this._renderLinkResults([]);
    } finally {
      if (this._linkSearchAbort === abort) this._linkSearchAbort = null;
    }
  }

  _abortLinkSearch() {
    if (this._linkSearchAbort) {
      try {
        this._linkSearchAbort.abort();
      } catch (_) {
        // noop
      }
      this._linkSearchAbort = null;
    }
  }

  _looksLikeUrl(value) {
    const s = (value || "").toString().trim();
    if (s === "") return false;
    if (s.startsWith("//")) return true;
    if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return true;
    if (s.includes(".") && !s.includes(" ")) return true;
    return false;
  }

  _renderLinkResults(pages) {
    if (!this.hasLinkResultsTarget) return;

    this.linkResultsTarget.innerHTML = "";
    this._linkItems = [];
    this._linkSelectedIndex = -1;

    const list = Array.isArray(pages) ? pages : [];
    if (list.length === 0) return;

    list.slice(0, 10).forEach((p) => this._appendLinkResultItem(p));
    this._moveLinkSelection(1);
  }

  _appendLinkResultItem(item) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className =
      "list-group-item list-group-item-action d-flex justify-content-between gap-2";

    const q =
      this.linkQueryTarget?.value?.toString?.().trim().toLowerCase?.() || "";
    const name = (item?.name || "").toString();
    const nameLower = name.toLowerCase();
    const titleMatch = q && nameLower.includes(q);

    const left = document.createElement("div");
    left.className = "d-flex flex-column gap-1 flex-grow-1";
    left.style.minWidth = "0";

    const titleRow = document.createElement("div");
    titleRow.className = "d-flex align-items-center gap-2";
    titleRow.style.minWidth = "0";

    const iconEl = this._buildIconEl(item);
    if (iconEl) titleRow.appendChild(iconEl);

    const titleText = document.createElement("span");
    titleText.textContent = name;
    titleRow.appendChild(titleText);

    left.appendChild(titleRow);

    const snippetHtml = (item?.snippet_html || "").toString();
    const previewText = (item?.preview_text || "").toString();
    const shouldShowMatchedSnippet =
      item?.type === "page" && snippetHtml && q && !titleMatch;
    const shouldShowTitlePreview =
      item?.type === "page" && titleMatch && previewText;

    if (shouldShowMatchedSnippet) {
      const snippet = document.createElement("div");
      snippet.className = "small text-body-secondary text-truncate";
      snippet.innerHTML = this._sanitizeSnippetHtml(snippetHtml);
      left.appendChild(snippet);
    } else if (shouldShowTitlePreview) {
      const snippet = document.createElement("div");
      snippet.className = "small text-body-secondary text-truncate";
      snippet.textContent = previewText;
      left.appendChild(snippet);
    }

    const right = document.createElement("span");
    right.className = "small text-body-secondary";
    right.textContent = (item?.wiki_title || "").toString();

    btn.appendChild(left);
    btn.appendChild(right);

    const idx = this._linkItems.length;
    btn.addEventListener("mouseenter", () => this._setLinkSelectedIndex(idx));
    btn.addEventListener("click", () => this._confirmPageResult(item));

    this.linkResultsTarget.appendChild(btn);
    this._linkItems.push({ el: btn, page: item });
  }

  _moveLinkSelection(delta) {
    if (!this._linkItems.length) return;
    const next =
      (this._linkSelectedIndex + delta + this._linkItems.length) %
      this._linkItems.length;
    this._setLinkSelectedIndex(next);
  }

  _setLinkSelectedIndex(idx) {
    this._linkItems.forEach((it) => it.el.classList.remove("active"));
    this._linkSelectedIndex = idx;
    const it = this._linkItems[idx];
    if (!it?.el) return;
    it.el.classList.add("active");
    try {
      it.el.scrollIntoView({ block: "nearest" });
    } catch (_) {
      // noop
    }
  }

  _buildIconEl(item) {
    const kind = (item?.icon_kind || "").toString();
    const colors = Array.isArray(item?.icon_color_classes)
      ? item.icon_color_classes
      : [];
    const extraColorClasses = colors
      .filter((c) => typeof c === "string" && c.trim())
      .join(" ");

    if (kind === "emoji" && item?.icon_emoji) {
      const span = document.createElement("span");
      span.className = [
        "page-icon",
        "page-icon--xs",
        "page-icon--emoji",
        extraColorClasses,
      ]
        .filter(Boolean)
        .join(" ");
      span.setAttribute("aria-hidden", "true");
      span.textContent = item.icon_emoji.toString();
      return span;
    }

    if (kind === "fa" && item?.icon_fa_classes) {
      const span = document.createElement("span");
      span.className = [
        "page-icon",
        "page-icon--xs",
        "page-icon--fa",
        extraColorClasses,
      ]
        .filter(Boolean)
        .join(" ");
      const i = document.createElement("i");
      i.className = item.icon_fa_classes.toString();
      span.appendChild(i);
      return span;
    }

    return null;
  }

  _sanitizeSnippetHtml(html) {
    const template = document.createElement("template");
    template.innerHTML = html.toString();

    const walker = document.createTreeWalker(
      template.content,
      NodeFilter.SHOW_ELEMENT,
      null
    );
    const toStrip = [];

    while (walker.nextNode()) {
      const el = walker.currentNode;
      if (el.tagName === "MARK") {
        Array.from(el.attributes).forEach((a) => el.removeAttribute(a.name));
      } else {
        toStrip.push(el);
      }
    }

    toStrip.forEach((el) => {
      const text = document.createTextNode(el.textContent || "");
      el.replaceWith(text);
    });

    return template.innerHTML;
  }

  _confirmPageResult(page) {
    if (!this._connected) return;
    if (!this.crepe) return;
    if (!this._pendingLink) return;

    const { from, to } = this._pendingLink;
    const name = (page?.name || "").toString().trim();
    if (!name) return;

    const normalized = this._normalizeAddressableName(name);
    const encoded = normalized.replaceAll(" ", "_");
    if (this._looksLikeUnsafeInternalDest(encoded)) return;

    const href = encodeURIComponent(encoded);
    this._applyOrReplaceLink({ from, to, href });
    this._hideLinkPopover();
  }

  _confirmTypedLink() {
    if (!this._connected) return;
    if (!this.crepe) return;
    if (!this._pendingLink) return;
    if (!this.hasLinkQueryTarget) return;

    const { from, to, href: existingHref } = this._pendingLink;
    const typed = this.linkQueryTarget.value.toString().trim();

    if (typed === "") {
      if (existingHref) this._removeLinkMark({ from, to });
      this._hideLinkPopover();
      return;
    }

    if (this._looksLikeUrl(typed)) {
      this._applyOrReplaceLink({ from, to, href: typed, insertText: typed });
      this._hideLinkPopover();
      return;
    }

    const normalized = this._normalizeAddressableName(typed);
    if (!normalized) return;
    const encoded = normalized.replaceAll(" ", "_");
    if (this._looksLikeUnsafeInternalDest(encoded)) return;
    const href = encodeURIComponent(encoded);
    this._applyOrReplaceLink({ from, to, href, insertText: normalized });
    this._hideLinkPopover();
  }

  _openLinkPopover({ from, to }) {
    if (!this._connected) return;
    if (!this.crepe) return;
    if (!this.hasLinkPopoverTarget) return;

    const ctx = this._readLinkContext({ from, to });
    if (!ctx) return;

    const existingHref = (ctx.href || "").toString().trim();
    this._pendingLink = { from: ctx.from, to: ctx.to, href: existingHref };

    // Prefer selected/link text as the starting point for search.
    const selectionText = (ctx.text || "").toString().trim();
    const fallback =
      existingHref && this._isInternalDest(existingHref)
        ? this._displayTitleFromInternalDest(existingHref)
        : existingHref;
    const initial = selectionText || fallback;

    if (this.hasLinkQueryTarget) this.linkQueryTarget.value = initial || "";
    this._renderLinkResults([]);

    this._showLinkPopoverAtSelection(ctx.from);
    this._attachOutsideClick();

    // Immediately search on open if we have a starting query (selection text is often a good hint).
    const q = (this.hasLinkQueryTarget ? this.linkQueryTarget.value : "")
      .toString()
      .trim();
    if (q) {
      if (this._linkSearchTimer) clearTimeout(this._linkSearchTimer);
      this._linkSearchTimer = null;
      this._runLinkAutocomplete(q);
    }
  }

  _showLinkPopoverAtSelection(pos) {
    if (!this.hasLinkPopoverTarget) return;
    const pop = this.linkPopoverTarget;
    this._linkPopoverOpen = true;

    let left = 12;
    let top = 12;

    try {
      this.crepe.editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        const coords = view.coordsAtPos(pos);
        left = Math.max(8, Math.min(window.innerWidth - 440, coords.left));
        top = Math.max(
          8,
          Math.min(window.innerHeight - 260, coords.bottom + 8)
        );
      });
    } catch (_) {
      // ignore
    }

    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
    pop.style.display = "";

    setTimeout(() => {
      if (!this._connected) return;
      try {
        this.linkQueryTarget?.focus?.();
        this.linkQueryTarget?.select?.();
      } catch (_) {
        // ignore
      }
    }, 0);
  }

  _hideLinkPopover() {
    if (this.hasLinkPopoverTarget)
      this.linkPopoverTarget.style.display = "none";
    this._linkPopoverOpen = false;
    this._pendingLink = null;
    if (this.hasLinkResultsTarget) this.linkResultsTarget.innerHTML = "";
    this._linkItems = [];
    this._linkSelectedIndex = -1;
    this._abortLinkSearch();
    this._detachOutsideClick();
  }

  _attachOutsideClick() {
    if (this._boundDocPointerDown) return;
    this._boundDocPointerDown = (event) => {
      if (!this._linkPopoverOpen) return;
      const t = event?.target;
      if (!t) return;
      if (this.linkPopoverTarget?.contains?.(t)) return;
      if (this.editorTarget?.contains?.(t)) return;
      this._hideLinkPopover();
    };
    document.addEventListener("pointerdown", this._boundDocPointerDown, true);
  }

  _detachOutsideClick() {
    if (!this._boundDocPointerDown) return;
    document.removeEventListener(
      "pointerdown",
      this._boundDocPointerDown,
      true
    );
    this._boundDocPointerDown = null;
  }

  _setupLinkKeyHandler() {
    if (this._boundLinkKeydown) return;
    if (!this.hasEditorTarget) return;

    this._boundLinkKeydown = (event) => {
      if (!this._connected) return;
      if (!this.crepe) return;
      if (this._mode !== "rich") return;
      const isK = event.key?.toLowerCase?.() === "k";
      const metaOrCtrl = event.metaKey || event.ctrlKey;
      if (!metaOrCtrl || !isK) return;
      // Prevent browser "insert link" behavior / default key handling.
      event.preventDefault();
      event.stopPropagation();

      this._openLinkPopoverForCurrentSelection();
    };

    this.editorTarget.addEventListener("keydown", this._boundLinkKeydown, true);
  }

  _teardownLinkKeyHandler() {
    if (this._boundLinkKeydown && this.hasEditorTarget) {
      this.editorTarget.removeEventListener(
        "keydown",
        this._boundLinkKeydown,
        true
      );
    }
    this._boundLinkKeydown = null;
  }

  _openLinkPopoverForCurrentSelection() {
    if (!this.crepe) return;
    this.crepe.editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const { from, to } = view.state.selection;
      this._openLinkPopover({ from, to });
    });
  }

  _applyOrReplaceLink({ from, to, href, insertText }) {
    if (!this.crepe) return;
    this.crepe.editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const linkType = linkSchema.type(ctx);
      const state = view.state;
      let tr = state.tr;

      const safeHref = (href || "").toString();
      if (safeHref.trim() === "") return;
      const safeInsert = (insertText || "").toString();

      let start = from;
      let end = to;

      if (from === to) {
        const insert = safeInsert.trim() !== "" ? safeInsert : safeHref;
        tr = tr.insertText(insert, from, to);
        start = from;
        end = from + insert.length;
      }

      tr = tr.removeMark(start, end, linkType);
      tr = tr.addMark(start, end, linkType.create({ href: safeHref }));

      view.dispatch(tr);
      view.focus();
    });

    this._syncFromEditorNow();
  }

  _removeLinkMark({ from, to }) {
    if (!this.crepe) return;
    this.crepe.editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const linkType = linkSchema.type(ctx);
      const state = view.state;
      let tr = state.tr;

      const start = Math.min(from, to);
      const end = Math.max(from, to);
      if (start === end) return;

      tr = tr.removeMark(start, end, linkType);
      view.dispatch(tr);
      view.focus();
    });

    this._syncFromEditorNow();
  }

  _readLinkContext({ from, to }) {
    if (!this.crepe) return null;
    try {
      let out = null;
      this.crepe.editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        const linkType = linkSchema.type(ctx);
        const state = view.state;
        const doc = state.doc;

        const selFrom = Number(from);
        const selTo = Number(to);
        const start = Math.max(0, Math.min(selFrom, selTo));
        const end = Math.max(start, Math.max(selFrom, selTo));

        // Selection text (best-effort).
        let text = "";
        try {
          text = doc.textBetween(start, end, "\n", "\n").trim();
        } catch (_) {
          text = "";
        }

        // Try to locate a link mark; support both ranged selections and cursor-in-link.
        let href = "";
        let linkRange = null;

        const findMarkInTextNode = (node) => {
          if (!node?.isText) return null;
          return linkType.isInSet(node.marks);
        };

        if (start === end) {
          const $pos = state.selection?.$from;
          const mark = linkType.isInSet(
            state.storedMarks || $pos?.marks?.() || []
          );
          if (mark) {
            href = mark.attrs?.href?.toString?.() || "";
            linkRange = this._linkRangeAroundCursor(state, linkType, start);
          }
        }

        if (!href) {
          doc.nodesBetween(start, end, (node, pos) => {
            const mark = findMarkInTextNode(node);
            if (!mark) return;
            href = mark.attrs?.href?.toString?.() || "";
            linkRange = { from: pos, to: pos + node.nodeSize };
            return false;
          });
        }

        if (linkRange) {
          out = {
            from: linkRange.from,
            to: linkRange.to,
            href,
            text: doc
              .textBetween(linkRange.from, linkRange.to, "\n", "\n")
              .trim(),
          };
        } else {
          out = { from: start, to: end, href: "", text };
        }
      });
      return out;
    } catch {
      return null;
    }
  }

  _linkRangeAroundCursor(state, linkType, cursorPos) {
    // Best-effort: find the contiguous run of text nodes in the current parent that share the same link href.
    try {
      const $pos = state.doc.resolve(cursorPos);
      const parent = $pos.parent;
      const parentStart = $pos.start();
      const cursor = cursorPos;

      const segments = [];
      parent.descendants((node, pos) => {
        if (!node.isText) return;
        const mark = linkType.isInSet(node.marks);
        if (!mark) return;
        const href = mark.attrs?.href?.toString?.() || "";
        const from = parentStart + pos;
        const to = from + node.nodeSize;
        segments.push({ from, to, href });
      });

      const hit = segments.find((s) => cursor >= s.from && cursor <= s.to);
      if (!hit) return null;

      let from = hit.from;
      let to = hit.to;

      // Expand to adjacent segments with same href.
      let changed = true;
      while (changed) {
        changed = false;
        const prev = segments.find((s) => s.to === from && s.href === hit.href);
        if (prev) {
          from = prev.from;
          changed = true;
        }
        const next = segments.find((s) => s.from === to && s.href === hit.href);
        if (next) {
          to = next.to;
          changed = true;
        }
      }

      return { from, to };
    } catch {
      return null;
    }
  }

  _isInternalDest(href) {
    const raw = (href || "").toString().trim();
    if (raw === "") return false;
    if (raw.startsWith("#")) return false;
    if (raw.startsWith("//")) return false;
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return false;
    const path = raw.replace(/^\//, "").split("#", 2)[0].split("?", 2)[0];
    return path !== "" && !path.includes("/") && !path.includes("\\");
  }

  _displayTitleFromInternalDest(href) {
    const raw = (href || "").toString().trim().replace(/^\//, "");
    const path = raw.split("#", 2)[0].split("?", 2)[0];
    try {
      const decoded = decodeURIComponent(path);
      return decoded.replaceAll("_", " ").replace(/\s+/g, " ").trim();
    } catch {
      return path.replaceAll("_", " ").replace(/\s+/g, " ").trim();
    }
  }

  _normalizeAddressableName(value) {
    let s = (value || "").toString();
    s = s.replaceAll("_", " ");
    s = s.replace(/\s+/g, " ").trim();
    return s === "" ? "" : s;
  }

  _looksLikeUnsafeInternalDest(urlName) {
    const s = (urlName || "").toString();
    if (s.includes("/") || s.includes("\\") || s.includes("..")) return true;
    if (s === "." || s === "..") return true;
    return false;
  }

  _initialMode() {
    const urlMode = this._modeFromUrl();
    if (urlMode) return urlMode;

    const v = this.hasModeValue ? this.modeValue?.toString?.() : null;
    return v === "markdown" ? "markdown" : "rich";
  }

  _modeFromUrl() {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get("editor") === "markdown" ? "markdown" : null;
    } catch {
      return null;
    }
  }

  async _setMode(mode, { updateUrl } = {}) {
    const desired = mode === "markdown" ? "markdown" : "rich";
    if (desired === this._mode) {
      if (updateUrl) this._replaceUrlForMode(desired);
      this._applyModeToUI();
      return;
    }

    this._mode = desired;

    if (desired === "markdown") {
      // Cancel any in-flight setup and ensure the textarea has the latest markdown.
      this._setupToken = (this._setupToken || 0) + 1;
      this._syncFromEditorNow();
      await this._destroyRichEditor();
      this._setupMarkdown();
    } else {
      this._destroyMarkdownEditor();
      if (this.hasEditorTarget) this.editorTarget.style.display = "";
      this._setup();
    }

    if (updateUrl) this._replaceUrlForMode(desired);
    this._applyModeToUI();
  }

  async _destroyRichEditor() {
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
      this._syncTimer = null;
    }

    if (this._boundSync && this.hasEditorTarget) {
      this.editorTarget.removeEventListener("input", this._boundSync, true);
      this.editorTarget.removeEventListener("keyup", this._boundSync, true);
      this.editorTarget.removeEventListener("blur", this._boundSync, true);
      this._boundSync = null;
    }

    if (this._boundSubmit && this._form) {
      this._form.removeEventListener("submit", this._boundSubmit);
      this._boundSubmit = null;
      this._form = null;
    }

    if (this.crepe) {
      try {
        await this.crepe.destroy();
      } catch (_) {
        // noop
      }
      this.crepe = null;
    }

    if (this.hasEditorTarget) {
      this.editorTarget.classList.remove("milkdown");
      this.editorTarget.innerHTML = "";
    }
  }

  _replaceUrlForMode(mode) {
    const base =
      (this.hasCanonicalUrlValue && this.canonicalUrlValue) ||
      window.location.href;
    try {
      const url = new URL(base, window.location.origin);
      if (mode === "markdown") {
        url.searchParams.set("editor", "markdown");
      } else {
        url.searchParams.delete("editor");
      }
      window.history.replaceState(
        {},
        "",
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch {
      // ignore
    }
  }

  _applyModeToUI() {
    const current = this._mode === "markdown" ? "markdown" : "rich";

    // Editor surface.
    if (current === "markdown") {
      if (this.hasEditorTarget) this.editorTarget.style.display = "none";
      if (this.hasMarkdownEditorTarget)
        this.markdownEditorTarget.style.display = "";
    } else {
      if (this.hasEditorTarget) this.editorTarget.style.display = "";
      if (this.hasMarkdownEditorTarget)
        this.markdownEditorTarget.style.display = "none";
      // textarea is hidden by _setup() when Crepe mounts; leave it visible until then.
    }

    // Toggle UI.
    if (this.hasModeOptionTarget) {
      this.modeOptionTargets.forEach((el) => {
        const active = el.dataset.mode === current;
        el.classList.toggle("active", active);
        el.setAttribute("aria-pressed", active ? "true" : "false");
      });
    }

    // Let other UI (e.g. header toggle) track current mode.
    try {
      window.dispatchEvent(
        new CustomEvent("wittle:milkdown:modeChanged", {
          detail: { mode: current },
        })
      );
    } catch (_) {
      // ignore
    }
  }

  _onEditorClick(event) {
    // In ProseMirror, a normal click often focuses the editor / edits selection.
    // Only navigate on cmd/ctrl-click for internal wiki links.
    const metaOrCtrl = event.metaKey || event.ctrlKey;
    if (!metaOrCtrl) return;
    if (!this.hasUserNameValue || !this.hasWikiNameValue) return;

    const a = event.target?.closest?.("a");
    if (!a) return;

    const href = (a.getAttribute("href") || "").trim();

    let name = "";
    let query = "";
    let fragment = "";

    if (href === "") {
      name = (a.textContent || "")
        .replaceAll("_", " ")
        .replace(/\s+/g, " ")
        .trim();
    } else {
      // Leave external/schemed links alone.
      if (href.startsWith("#")) return;
      if (href.startsWith("//")) return;
      if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return;

      // If it's an absolute path with multiple segments, treat it as an app path, not a wiki page.
      if (href.startsWith("/") && href.slice(1).includes("/")) return;

      let raw = href.startsWith("/") ? href.slice(1) : href;

      // Split query/fragment (preserve both).
      const hashSplit = raw.split("#", 2);
      raw = hashSplit[0] || "";
      fragment = hashSplit.length === 2 ? hashSplit[1] : "";

      const querySplit = raw.split("?", 2);
      raw = querySplit[0] || "";
      query = querySplit.length === 2 ? querySplit[1] : "";

      // Block traversal / path-ish links.
      if (
        raw.includes("/") ||
        raw.includes("\\") ||
        raw === "." ||
        raw === ".." ||
        raw.includes("..")
      )
        return;

      try {
        raw = decodeURIComponent(raw);
      } catch {
        return;
      }

      name = raw.replaceAll("_", " ").replace(/\s+/g, " ").trim();
    }

    if (!name) return;

    // Match AddressableName.encode_url_name: collapse whitespace to underscores.
    const pageName = name.replaceAll(" ", "_");
    let url = `/${encodeURIComponent(this.userNameValue)}/${encodeURIComponent(
      this.wikiNameValue
    )}/${encodeURIComponent(pageName)}`;
    if (query) url += `?${query}`;
    if (fragment) url += `#${fragment}`;

    event.preventDefault();
    event.stopPropagation();
    window.location.assign(url);
  }

  _setupMarkdown() {
    if (!this._connected) return;
    if (!this.hasMarkdownEditorTarget || !this.hasInputTarget) return;
    if (this._cmView) return;
    if (this._mode !== "markdown") return;

    this.markdownEditorTarget.innerHTML = "";
    this.markdownEditorTarget.classList.add("cm-host");

    // Keep the underlying textarea hidden; it remains the source of truth for form submission.
    // If CodeMirror fails to mount for any reason, fall back to showing the textarea.
    try {
      const isDark = this._bootstrapTheme() === "dark";

      const extensions = [
        highlightSpecialChars(),
        history(),
        drawSelection(),
        highlightActiveLine(),
        EditorView.lineWrapping,
        keymap.of([indentWithTab, ...defaultKeymap, ...historyKeymap]),
        markdown({ codeLanguages: languages }),
        syntaxHighlighting(
          isDark ? oneDarkHighlightStyle : defaultHighlightStyle,
          { fallback: true }
        ),
        EditorView.updateListener.of((v) => {
          if (!v.docChanged) return;
          this.inputTarget.value = v.state.doc.toString();
        }),
        EditorView.theme(
          {
            "&": {
              backgroundColor: "transparent",
              color: "var(--bs-body-color)",
            },
            ".cm-content": {
              padding: "0.75rem 1rem",
              caretColor: "var(--bs-body-color)",
            },
            ".cm-gutters": {
              backgroundColor: "transparent",
              color: "var(--bs-secondary-color)",
              borderRight: "1px solid var(--bs-border-color)",
            },
            "&.cm-focused": {
              outline: "none",
            },
            "&.cm-editor": {
              borderRadius: "inherit",
              border: "none",
            },
          },
          { dark: false }
        ),
      ];

      const state = EditorState.create({
        doc: this.inputTarget.value || "",
        extensions,
      });

      this._cmView = new EditorView({
        state,
        parent: this.markdownEditorTarget,
      });

      this.inputTarget.style.display = "none";
    } catch (_) {
      this.markdownEditorTarget.innerHTML = "";
      this._cmView = null;
      this.inputTarget.style.display = "";
    }
  }

  _destroyMarkdownEditor() {
    if (this._cmView) {
      try {
        this._cmView.destroy();
      } catch (_) {
        // noop
      }
      this._cmView = null;
    }
    if (this.hasMarkdownEditorTarget) {
      this.markdownEditorTarget.innerHTML = "";
    }
  }

  _bootstrapTheme() {
    const v = document.documentElement?.dataset?.bsTheme;
    return v === "dark" ? "dark" : "light";
  }
}
